<template>
  <v-data-table
    :items="items"
    :headers="headers"
    :server-items-length="total"
    :options.sync="options"
    :loading="loading"
    :expanded.sync="expanded"
    fixed-header
    single-expand
    show-expand
    :height="tableHeight"
    @pagination="updatePage"
    @item-expanded="fetchClaimDetail"
  >
    <template v-slot:expanded-item="{ headers, item }">
      <td :colspan="headers.length">
        <v-progress-circular indeterminate color="primary" v-if="loading"></v-progress-circular>
        <span v-else>
          <v-simple-table dense class="mt-5 mb-5">
            <template v-slot:default>
              <tbody>
                <tr v-if="!item.approved && item.rejectedDate">
                  <th width="30%" class="text-right">
                    Rejected Date:
                  </th>
                  <td width="70%">
                    {{ item.rejectedDate }}
                  </td>
                </tr>
                <tr v-if="!item.approved && item.rejectedReason">
                  <th width="30%" class="text-right">
                    Rejected Reason:
                  </th>
                  <td width="70%">
                    {{ item.rejectedReason }}
                  </td>
                </tr>
                <tr v-for="claimFieldValue in expandedClaimFieldValues" :key="claimFieldValue.id">
                  <th width="30%" class="text-right">
                    {{ claimFieldValue.claimField.name }}
                  </th>
                  <td width="70%">
                    {{ claimFieldValue.valueFormatted }}
                  </td>
                </tr>
                <tr v-for="(claimUpload, i) in expandedClaimUploads" :key="i + '-uploads'">
                  <th width="30%" class="text-right">File {{ i + 1 }}</th>
                  <td width="70%">
                    <a :href="claimUpload.href" target="_blank" rel="noreferrer">{{ claimUpload.originalFilename }}</a>
                    ({{ claimUpload.contentType }})
                  </td>
                </tr>
              </tbody>
            </template>
          </v-simple-table>
          <v-data-table
            dense
            class="mt-5 mb-5"
            :calculate-widths="true"
            hide-default-footer
            :headers="productHeaders"
            :items="expandedClaimProducts[item.id]"
          >
            <template v-slot:item.calculatedTotalPayoutAmount="{ item }">
              {{ item.calculatedTotalPayoutAmount | toNumber(2, selectedProgram) }}
              {{ item.calculatedTotalPayoutAmountCurrency ? item.calculatedTotalPayoutAmountCurrency : "" }}
            </template>
          </v-data-table>
        </span>
      </td>
    </template>
    <template v-slot:item.claimKey="{ item }">
      <v-btn
        v-if="!item.submitted"
        :to="{ name: 'cprClaim', params: { id: item.promotion.id }, query: { claimId: item.id } }"
        color="primary"
      >
        {{ item.claimKey }}
      </v-btn>
      <span v-else> {{ item.claimKey }}</span>
    </template>
    <template v-slot:item.calculatedTotalClaimAmount="{ item }">
      {{ item.calculatedCalculatedTotalClaimAmount | toNumber(2, selectedProgram) }}
      {{ item.calculatedCalculatedTotalClaimAmountCurrency ? item.calculatedCalculatedTotalClaimAmountCurrency : "" }}
    </template>
    <template v-slot:item.createdDate="{ item }">
      {{ item.updatedDate | formatDateClient("MM/DD/YYYY", selectedClient) }}
    </template>
    <template v-slot:item.invoiceDate="{ item }">
      {{ item.invoiceDate | formatDateClient("MM/DD/YYYY", selectedClient) }}
    </template>
    <template v-slot:item.claimStage.name="{ item }">
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <span v-bind="attrs" v-on="on">
            {{ item.claimStage | formatStage(item) }}
          </span>
        </template>
        <span>{{ item.claimStage.description }}</span>
      </v-tooltip>
    </template>
  </v-data-table>
</template>

<script>
import { mapGetters } from "vuex";
import ApiService from "../../services/api.service";

export default {
  name: "ClaimRejectedTable",
  props: {
    filters: Object,
    showParticipant: {
      type: Boolean,
      default: false
    }
  },
  data: () => ({
    items: [],
    tableHeight: null,
    headers: [
      {
        text: "Detail",
        value: "data-table-expand",
        show: true
      },
      {
        value: "createdDate",
        text: "Date Submitted",
        sortable: true,
        show: true
      },
      {
        value: "claimKey",
        text: "Claim Number",
        sortable: true,
        show: true
      },
      {
        value: "promotion.promotionKey",
        text: "Promotion Number",
        sortable: false,
        show: true
      },
      {
        value: "promotion.name",
        text: "Promotion",
        sortable: false,
        show: true
      },
      {
        value: "invoiceNumber",
        text: "Invoice Number",
        sortable: true,
        show: true
      },
      {
        value: "invoiceDate",
        text: "Invoice Date",
        sortable: true,
        show: true
      },
      {
        value: "calculatedTotalClaimAmount",
        text: "Amount",
        sortable: true,
        align: "right",
        show: true
      },
      {
        value: "claimStage.name",
        text: "Status",
        sortable: false,
        show: true
      },
      {
        value: "rejectedReason",
        text: "Reason"
      }
    ],
    expanded: [],
    expandedClaimProducts: [],
    expandedClaimFieldValues: [],
    expandedClaimUploads: [],
    productHeaders: [
      {
        value: "promotionProduct.product.productKey",
        text: "Product Number",
        sortable: true
      },
      {
        value: "promotionProduct.product.name",
        text: "Product Name",
        sortable: true
      },
      {
        value: "promotionProduct.product.productCategory.name",
        text: "Product Category",
        sortable: true
      },
      {
        value: "serialNumber",
        text: "Serial Number",
        sortable: true
      },
      {
        value: "quantity",
        text: "Quantity",
        sortable: true,
        align: "center"
      },
      {
        value: "calculatedTotalPayoutAmount",
        text: "Amount",
        sortable: true,
        align: "right"
      }
    ],
    loading: false,
    total: 0,
    options: {
      sortBy: ["createdDate"],
      sortDesc: [true],
      itemsPerPage: 10,
      page: 1
    }
  }),
  watch: {
    options: {
      handler() {
        this.initialize();
      },
      deep: true
    },
    filters: {
      handler() {
        this.initialize();
      },
      deep: true
    }
  },
  methods: {
    initialize() {
      this.loading = true;
      const { sortBy, sortDesc, page, itemsPerPage } = this.options;

      let filters = Object.assign({}, this.filters);
      filters.submitted = true;
      filters.rejected = true;
      filters.closed = true;

      ApiService.post(
        "/api/claims/search?size=" +
          itemsPerPage +
          "&page=" +
          (page - 1) +
          (sortBy && sortBy.length > 0
            ? "&sort=" + sortBy[0] + "," + ((sortDesc && sortDesc.length > 0) & sortDesc[0] ? "DESC" : "ASC")
            : ""),
        filters
      )
        .then(({ data }) => {
          this.loading = false;
          this.items = data.content;
          this.total = data.totalElements;
          this.$emit("totalElements", this.total);
        })
        .catch(() => {
          this.loading = false;
        });
    },
    updatePage(pagination) {
      if (pagination.itemsPerPage == 50) {
        this.tableHeight = "30vh";
      } else {
        this.tableHeight = null;
      }
    },
    fetchClaimDetail(obj) {
      this.loading = true;
      return Promise.all([
        this.fetchClaimProducts(obj.item),
        this.fetchClaimFieldValues(obj.item),
        this.fetchClaimUploads(obj.item)
      ]).finally(() => {
        this.loading = false;
      });
    },
    fetchClaimProducts(claim) {
      if (claim.id in this.expandedClaimProducts) {
        return Promise.resolve();
      }
      return ApiService.get("/api/claimProducts/byClaimId/" + claim.id).then(({ data }) => {
        this.expandedClaimProducts[claim.id] = data.content;
      });
    },
    fetchClaimFieldValues(claim) {
      this.expandedClaimFieldValues = [];
      return ApiService.get("/api/claimFieldValues/byClaimId/" + claim.id + "?size=100").then(({ data }) => {
        let claimFieldValues = data.content;
        claimFieldValues.sort((a, b) => a.claimField.rankOrder - b.claimField.rankOrder);
        this.expandedClaimFieldValues = claimFieldValues;
      });
    },
    fetchClaimUploads(claim) {
      this.expandedClaimUploads = [];
      return ApiService.get("/api/claims/" + claim.id + "/uploads").then(({ data }) => {
        this.expandedClaimUploads = data._embedded.uploads;
      });
    }
  },
  mounted() {
    if (this.showParticipant) {
      this.headers.splice(1, 0, {
        value: "participant.fullName",
        text: "Participant",
        sortable: false
      });
    }
  },
  computed: {
    ...mapGetters(["selectedParticipant", "selectedClient", "selectedProgram"])
  }
};
</script>
